// Squad-chat photo pipeline: pick (native camera/library, web file input) →
// downscale/compress on-device → upload to Storage under the membership-gated
// chat/{squadId}/ path → return the download URL to embed in the message.
//
// This is deliberately the v1 of all photo UGC in the app — public map stories
// later reuse this same pick→compress→upload spine with a different path/rules.
import { Capacitor } from '@capacitor/core';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const MAX_EDGE = 1280;   // px, longest side after downscale
const JPEG_Q = 0.75;     // quality — keeps typical shots well under the 2MB rule cap

async function pickImageDataUrl() {
  if (Capacitor.isNativePlatform?.()) {
    // Native: OS sheet offering Camera or Photo Library.
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
    // Settle permissions BEFORE the pick: on first run the iOS permission
    // prompt otherwise lands mid-flow and the pick result gets swallowed
    // (user's first photo "didn't take"). In-context request — user just
    // tapped the camera button.
    try { await Camera.requestPermissions({ permissions: ['camera', 'photos'] }); } catch { /* getPhoto will surface real denials */ }
    try {
      const shot = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
        quality: 85,
        width: 1600, // pre-scale big sensors down before we compress further
        correctOrientation: true,
      });
      return shot.dataUrl || null;
    } catch (e) {
      // Backing out of the sheet/camera is a cancel, not an error.
      if (/cancel/i.test(e?.message || '')) return null;
      throw e;
    }
  }
  // Web: plain file input (no extra UI deps).
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const f = input.files && input.files[0];
      if (!f) return resolve(null);
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(f);
    };
    // If the user dismisses the picker, no event fires — resolve on focus return.
    window.addEventListener('focus', () => setTimeout(() => resolve(null), 500), { once: true });
    input.click();
  });
}

function compressToBlob(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('compress failed'))),
        'image/jpeg',
        JPEG_Q
      );
    };
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = dataUrl;
  });
}

/**
 * Stage 1 — pick + compress only (nothing uploaded yet, so cancelling or
 * discarding the staged photo costs nothing). Returns { blob, previewUrl }
 * or null if the user cancelled the picker.
 */
export async function pickAndCompressPhoto() {
  const dataUrl = await pickImageDataUrl();
  if (!dataUrl) return null;
  const blob = await compressToBlob(dataUrl);
  return { blob, previewUrl: URL.createObjectURL(blob) };
}

/**
 * Stage 2 — upload a staged blob. Returns the download URL.
 * Throws on failure (caller shows the error state).
 */
export async function uploadChatPhoto({ squadId, uid, blob }) {
  const path = `chat/${squadId}/${uid}_${Date.now()}.jpg`;
  const storageRef = ref(getStorage(), path);
  await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
  return getDownloadURL(storageRef);
}
